import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';
import { useTask } from '../../context/TaskContext';
import { useCompanyWorkForce } from '../../context/CompanyWorkForceContext';
import Breadcrumb from '../common/Breadcrumb';

// Validation schema
const schema = yup.object().shape({
  type: yup.string().required('Task type is required'),
  startDate: yup.date().required('Start date is required'),
  endDate: yup.date().required('End date is required'),
  endTime: yup.string().required('End time is required'),
  assignTo: yup.array().min(1, 'At least one assignee is required'),
  privacy: yup.boolean(),
  recurring: yup.boolean(),
  repeatType: yup.string().when('recurring', {
    is: true,
    then: () => yup.string().required('Repeat type is required'),
    otherwise: () => yup.string().nullable(),
  }),
  repeatEvery: yup.number().when('recurring', {
    is: true,
    then: () => yup.number().required('Repeat frequency is required').min(1, 'Must be at least 1'),
    otherwise: () => yup.number().nullable(),
  }),
  repeatDays: yup.array().when(['recurring', 'repeatType'], {
    is: (recurring, repeatType) => recurring && repeatType === 'weekly',
    then: () => yup.array().min(1, 'Select at least one day'),
    otherwise: () => yup.array().nullable(),
  }),
  repeatBy: yup.string().when(['recurring', 'repeatType'], {
    is: (recurring, repeatType) => recurring && repeatType === 'monthly',
    then: () => yup.string().required('Select how to repeat'),
    otherwise: () => yup.string().nullable(),
  }),
  endType: yup.string().when('recurring', {
    is: true,
    then: () => yup.string().required('Select end type'),
    otherwise: () => yup.string().nullable(),
  }),
  endAfter: yup.number().when(['recurring', 'endType'], {
    is: (recurring, endType) => recurring && endType === 'after',
    then: () => yup.number().required('Number of occurrences is required').min(1, 'Must be at least 1'),
    otherwise: () => yup.number().nullable(),
  }),
  recurringEndDate: yup.date().when(['recurring', 'endType'], {
    
    is: (recurring, endType) => recurring && endType === 'on',
    then: () => yup.string().required('End date is required'),
    otherwise: () => yup.string().nullable(),
  }),
  description: yup.string(),
});

const TaskForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { createTask, updateTask, getTaskById, isLoading: contextLoading } = useTask();
  const { workForce } = useCompanyWorkForce();
  const [isLoading, setIsLoading] = useState(true);
  const [showRecurringOptions, setShowRecurringOptions] = useState(false);
  const [selectedRepeatDays, setSelectedRepeatDays] = useState([]);
  const [workforceOptions, setWorkforceOptions] = useState([]);
  const isEditMode = Boolean(id);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      type: '',
      startDate: '',
      endDate: '',
      endTime: '',
      assignTo: [],
      privacy: false,
      recurring: false,
      repeatType: '',
      repeatEvery: 1,
      repeatDays: [],
      repeatBy: '',
      endType: 'never',
      endAfter: 1,
      recurringEndDate: '',
      description: '',
    },
  });

  // Watch all recurring-related fields
  const recurring = watch('recurring');
  const repeatType = watch('repeatType');
  const repeatEvery = watch('repeatEvery');
  const watchedRepeatDays = watch('repeatDays');
  const repeatBy = watch('repeatBy');
  const endType = watch('endType');
  const endAfter = watch('endAfter');
  const recurringEndDate = watch('recurringEndDate');
  const startDate = watch('startDate');

  const loadWorkforce = useCallback(async () => {
    try {
      const options = workForce.map(person => ({
        value: person.id,
        label: `${person.firstName} ${person.lastName}`
      }));
      setWorkforceOptions(options);
    } catch (error) {
      console.error('Error loading workforce:', error);
      toast.error('Failed to load workforce data');
    }
  }, [workForce]);

  useEffect(() => {
    const loadData = async () => {
      try {
        await loadWorkforce();
        if (isEditMode) {
          // Wait for tasks to be loaded
          if (contextLoading) {
            return;
          }
          
          const task = getTaskById(id);
          if (task) {
            // Format dates for input fields
            const formattedTask = {
              ...task,
              startDate: task.startDate ? new Date(task.startDate).toISOString().split('T')[0] : '',
              endDate: task.endDate ? new Date(task.endDate).toISOString().split('T')[0] : '',
              recurringEndDate: task.recurringEndDate ? new Date(task.recurringEndDate).toISOString().split('T')[0] : '',
              // Convert single assignee to array if needed
              assignTo: Array.isArray(task.assignTo) ? task.assignTo : task.assignTo ? [task.assignTo] : [],
            };
    
            Object.keys(formattedTask).forEach(key => {
              setValue(key, formattedTask[key]);
            });
            
            if (task.repeatDays) {
              setSelectedRepeatDays(task.repeatDays);
            }
          } else {
            toast.error('Task not found');
            navigate('/tasks');
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isEditMode, loadWorkforce, getTaskById, id, navigate, setValue, contextLoading]);

  useEffect(() => {
    if (recurring && startDate) {
      setShowRecurringOptions(true);
    } else {
      setShowRecurringOptions(false);
      if (!recurring) {
        setValue('repeatType', '');
        setValue('repeatEvery', 1);
        setValue('repeatDays', []);
        setValue('repeatBy', '');
        setValue('endType', 'never');
        setValue('endAfter', 1);
        setValue('recurringEndDate', '');
        setSelectedRepeatDays([]);
      }
    }
  }, [recurring, startDate, setValue]);

   
  const handleRepeatDayChange = useCallback((day) => {
    const currentDays = watch('repeatDays') || [];
    const updatedDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day];
    setValue('repeatDays', updatedDays);
    setSelectedRepeatDays(updatedDays);
  }, [watch, setValue]);

  const handleCancel = useCallback(() => {
    reset();
    navigate('/tasks');
  }, [reset, navigate]);

  const getSummaryMessage = useCallback((data) => {
    if (!data.recurring) return null;

    let summaryMessage = 'This task repeats ';
    
    // Add frequency
    if (data.repeatType === 'daily') {
      summaryMessage += `every ${data.repeatEvery} day${data.repeatEvery > 1 ? 's' : ''}`;
    } else if (data.repeatType === 'weekly') {
      summaryMessage += `every ${data.repeatEvery} week${data.repeatEvery > 1 ? 's' : ''}`;
      if (data.repeatDays && data.repeatDays.length > 0) {
        const days = data.repeatDays.map(day => {
          switch(day) {
            case 'M': return 'Monday';
            case 'T': return 'Tuesday';
            case 'W': return 'Wednesday';
            case 'Th': return 'Thursday';
            case 'F': return 'Friday';
            case 'Sa': return 'Saturday';
            case 'Su': return 'Sunday';
            default: return day;
          }
        });
        summaryMessage += ` on ${days.join(', ')}`;
      }
    } else if (data.repeatType === 'monthly') {
      summaryMessage += `every ${data.repeatEvery} month${data.repeatEvery > 1 ? 's' : ''}`;
      if (data.repeatBy === 'day') {
        summaryMessage += ' on the same day';
      } else if (data.repeatBy === 'weekday') {
        const startDate = new Date(data.startDate);
        const dayOfWeek = startDate.toLocaleDateString('en-US', { weekday: 'long' });
        const weekNumber = Math.ceil(startDate.getDate() / 7);
        const weekOrdinal = ['First', 'Second', 'Third', 'Fourth', 'Fifth'][weekNumber - 1];
        summaryMessage += ` on the ${weekOrdinal} ${dayOfWeek} of the month`;
      }
    } else if (data.repeatType === 'yearly') {
      summaryMessage += `every ${data.repeatEvery} year${data.repeatEvery > 1 ? 's' : ''}`;
    }

    // Add end condition
    if (data.endType === 'after') {
      summaryMessage += ` for ${data.endAfter} occurrence${data.endAfter > 1 ? 's' : ''}`;
    } else if (data.endType === 'on' && data.recurringEndDate) {
      const endDate = new Date(data.recurringEndDate);
      summaryMessage += ` until ${endDate.toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: 'numeric' 
      })}`;
    } else if (data.endType === 'never') {
      summaryMessage += ' indefinitely';
    }

    return summaryMessage;
  }, []);

  // Get current form values for summary
  const getCurrentSummary = useCallback(() => {
    const currentValues = {
      recurring,
      repeatType,
      repeatEvery,
      repeatDays: watchedRepeatDays,
      repeatBy,
      endType,
      endAfter,
      recurringEndDate,
      startDate
    };
    return getSummaryMessage(currentValues);
  }, [
    recurring,
    repeatType,
    repeatEvery,
    watchedRepeatDays,
    repeatBy,
    endType,
    endAfter,
    recurringEndDate,
    startDate,
    getSummaryMessage
  ]);

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      
      // Format dates before submission
      const formattedData = {
        ...data,
        startDate: data.startDate ? new Date(data.startDate).toISOString() : null,
        endDate: data.endDate ? new Date(data.endDate).toISOString() : null,
        recurringEndDate: data.recurringEndDate ? new Date(data.recurringEndDate).toISOString() : null,
      };
      
      if (isEditMode) {
        await updateTask(id, formattedData);
      } else {
        await createTask(formattedData);
      }

      // Show summary message
      const summaryMessage = getCurrentSummary();
      if (summaryMessage) {
        toast.success(summaryMessage);
      }
      
      navigate('/tasks');
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} task`);
    } finally {
      setIsLoading(false);
    }
  };

  // Add error logging
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log('Form Validation Errors:', {
        basic: {
          type: errors.type?.message,
          startDate: errors.startDate?.message,
          endDate: errors.endDate?.message,
          endTime: errors.endTime?.message,
          assignTo: errors.assignTo?.message,
        },
        recurring: {
          repeatType: errors.repeatType?.message,
          repeatEvery: errors.repeatEvery?.message,
          repeatDays: errors.repeatDays?.message,
          repeatBy: errors.repeatBy?.message,
          endType: errors.endType?.message,
          endAfter: errors.endAfter?.message,
          recurringEndDate: errors.recurringEndDate?.message,
        }
      });
    }
  }, [errors]);

  if (isLoading || contextLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb />
      <div className="mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {isEditMode ? 'Edit Task' : 'Create New Task'}
        </h1>
      </div>

      <div className="bg-white rounded-lg shadow">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6">
          {/* Task Information */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Task Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Task Type</label>
                <select
                  {...register('type')}
                  className={`mt-1 block w-full px-3 py-2 border ${errors.type ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                >
                  <option value="">Select Type</option>
                  <option value="call">Call</option>
                  <option value="email">Email</option>
                  <option value="misc">Misc</option>
                </select>
                {errors.type && (
                  <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Assign To</label>
                <Select
                  isMulti
                  options={workforceOptions}
                  value={workforceOptions.filter(option => 
                    (watch('assignTo') || []).includes(option.value)
                  )}
                  onChange={(selectedOptions) => {
                    const newValue = selectedOptions ? selectedOptions.map(option => option.value) : [];
                    setValue('assignTo', newValue, { 
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true
                    });
                  }}
                  onBlur={() => {
                    trigger('assignTo');
                  }}
                  className={`mt-1 block w-full ${errors.assignTo ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                  classNamePrefix="select"
                  placeholder="Select assignees..."
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: errors.assignTo ? '#EF4444' : state.isFocused ? '#009efb' : '#D1D5DB',
                      boxShadow: state.isFocused ? '0 0 0 1px #009efb' : 'none',
                      '&:hover': {
                        borderColor: errors.assignTo ? '#EF4444' : '#009efb'
                      }
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? '#009efb' : state.isFocused ? '#F3F4F6' : 'white',
                      color: state.isSelected ? 'white' : '#374151',
                      '&:active': {
                        backgroundColor: '#0089db'
                      }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#E5E7EB',
                      borderRadius: '0.375rem',
                      padding: '0.125rem 0.375rem'
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: '#374151',
                      fontSize: '0.875rem'
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: '#6B7280',
                      '&:hover': {
                        backgroundColor: '#D1D5DB',
                        color: '#374151'
                      }
                    })
                  }}
                />
                {errors.assignTo && (
                  <p className="mt-1 text-sm text-red-600">{errors.assignTo.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                <input
                  type="date"
                  {...register('startDate')}
                  className={`mt-1 block w-full px-3 py-2 border ${errors.startDate ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                />
                {errors.startDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.startDate.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">End Date</label>
                <input
                  type="date"
                  {...register('endDate')}
                  className={`mt-1 block w-full px-3 py-2 border ${errors.endDate ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                />
                {errors.endDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.endDate.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">End Time</label>
                <input
                  type="time"
                  {...register('endTime')}
                  className={`mt-1 block w-full px-3 py-2 border ${errors.endTime ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                />
                {errors.endTime && (
                  <p className="mt-1 text-sm text-red-600">{errors.endTime.message}</p>
                )}
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                {...register('description')}
                rows="4"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                placeholder="Enter task description..."
              />
            </div>

            <div className="flex space-x-6 mt-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('privacy')}
                  className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">Everyone can see this task</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('recurring')}
                  disabled={!startDate}
                  className={`h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded ${
                    !startDate ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                />
                <label className={`ml-2 text-sm text-gray-700 ${
                  !startDate ? 'opacity-50' : ''
                }`}>
                  Recurring (Repeat This Task)
                  {!startDate && <span className="ml-1 text-xs text-gray-500">(Select start date first)</span>}
                </label>
              </div>
            </div>
          </div>

          {/* Recurring Options */}
          {showRecurringOptions && (
            <div className="border-t border-gray-200 pt-4">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Recurring Options</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Repeats</label>
                  <select
                    {...register('repeatType')}
                    className={`mt-1 block w-full px-3 py-2 border ${errors.repeatType ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                  >
                    <option value="">Select Type</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                  {errors.repeatType && (
                    <p className="mt-1 text-sm text-red-600">{errors.repeatType.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Repeat Every
                    {repeatType === 'daily' && ' (Days)'}
                    {repeatType === 'weekly' && ' (Weeks)'}
                    {repeatType === 'monthly' && ' (Months)'}
                    {repeatType === 'yearly' && ' (Years)'}
                  </label>
                  <input
                    type="number"
                    {...register('repeatEvery')}
                    min="1"
                    className={`mt-1 block w-full px-3 py-2 border ${errors.repeatEvery ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                  />
                  {errors.repeatEvery && (
                    <p className="mt-1 text-sm text-red-600">{errors.repeatEvery.message}</p>
                  )}
                </div>
              </div>

              {/* Weekly Repeat Days */}
              {repeatType === 'weekly' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Repeat On</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {['M', 'T', 'W', 'Th', 'F', 'Sa', 'Su'].map((day, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleRepeatDayChange(day)}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${
                          selectedRepeatDays.includes(day)
                            ? 'bg-[#009efb] text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                  {errors.repeatDays && (
                    <p className="mt-1 text-sm text-red-600">{errors.repeatDays.message}</p>
                  )}
                </div>
              )}

              {/* Monthly Repeat Options */}
              {repeatType === 'monthly' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Repeat By</label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        {...register('repeatBy')}
                        value="day"
                        className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                      />
                      <label className="ml-2 text-sm text-gray-700">Day of the month</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        {...register('repeatBy')}
                        value="weekday"
                        className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                      />
                      <label className="ml-2 text-sm text-gray-700">Day of the week</label>
                    </div>
                  </div>
                  {errors.repeatBy && (
                    <p className="mt-1 text-sm text-red-600">{errors.repeatBy.message}</p>
                  )}
                </div>
              )}

              {/* End Options */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">Ends</label>
                <div className="mt-2 space-y-4">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      {...register('endType')}
                      value="never"
                      className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                    />
                    <label className="ml-2 text-sm text-gray-700">Never</label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="radio"
                      {...register('endType')}
                      value="after"
                      className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                    />
                    <label className="ml-2 text-sm text-gray-700">After</label>
                    {endType === 'after' && (
                      <div className="ml-4">
                        <input
                          type="number"
                          {...register('endAfter')}
                          min="1"
                          className={`block w-20 px-3 py-2 border ${errors.endAfter ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                        />
                        <span className="ml-2 text-sm text-gray-500">occurrences</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center">
                    <input
                      type="radio"
                      {...register('endType')}
                      value="on"
                      className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                    />
                    <label className="ml-2 text-sm text-gray-700">On</label>
                    {endType === 'on' && (
                      <div className="ml-4">
                        <input
                          type="date"
                          {...register('recurringEndDate')}
                          className={`block px-3 py-2 border ${errors.recurringEndDate ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                        />
                        {errors.recurringEndDate && (
                          <p className="mt-1 text-sm text-red-600">{errors.recurringEndDate.message}</p>
                      )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
                <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">Summary</label>
                    <div className="mt-2 space-y-4">
                        {/* Live Summary */}
                        {recurring && getCurrentSummary() && (
                            <div className="mt-4 bg-green-50 border border-green-400 text-green-700 px-4 py-3 rounded-md">
                             <p className="text-sm font-medium">{getCurrentSummary()}</p>
                             </div>
                          
                        )}
                    </div>
                 </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="mt-6 flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
            >
              {isEditMode ? 'Update Task' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskForm; 